import React from "react";
import useAuth from "containers/auth/useHooks";
import { AMBASSADOR_APIS } from "libs/apis/ambassador.api";
import { useNavigate } from "react-router-dom";
import { successToaster } from "utils/helpers/common/alert-service";
import { siteRoutes } from "utils/helpers/enums/routes";
import { CompleteAmbassadorProfileDTO } from "utils/helpers/models/ambassador/complete-profile.dto";
import { EditProfileAmbassador } from "utils/helpers/models/ambassador/edit-profile.dto";
import { BadgeDTO } from "utils/helpers/models/host-dtos/badge.dto";
import { EventsFilterDTO } from "utils/helpers/models/host-dtos/events-filter.dto";
import { AmbassadorBadgeDTO } from "utils/helpers/models/shared/amdassadorbadge.dto";
import { PictureDTO } from "utils/helpers/models/shared/picture.dto";
import useStore from "hooks/useStore";
import { AdminEventDTO } from "utils/helpers/models/shared/admin-event.dto";
import { EventDTO } from "utils/helpers/models/shared/event.dto";

const useAmbassador = () => {
  const { getUserProfile } = useAuth();
  const navigate = useNavigate();
  const { updateProfileCompletionStatus } = useStore();
  const updateAmbassadorProfile = async (
    body: CompleteAmbassadorProfileDTO
  ) => {
    const response = await AMBASSADOR_APIS.updateAmbassadorProfile(body);
    const { status = false } = response || {};
    if (status) {
      getUserProfile();
      navigate(siteRoutes.ambassadorWelcome);
    }
  };
  const updateUserProfile = async (
    body: CompleteAmbassadorProfileDTO,
    formData: CompleteAmbassadorProfileDTO,
    setOldFormData: Function,
    setCurrentStep: Function
  ) => {
    const response = await AMBASSADOR_APIS.updateUserProfile(body);
    const { status = false } = response || {};
    if (status) {
      setOldFormData({ ...formData });
      setCurrentStep((currentStep) => currentStep + 1);
      getUserProfile();
    }
  };

  const uploadFile = async (
    file: File
  ): Promise<PictureDTO | { error?: string } | null> => {
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await AMBASSADOR_APIS.uploadFile(body);
      const { status = false, data, message = "" } = response || {};
      if (status && data) {
        return new PictureDTO(data);
      } else {
        // Return error message if available
        return {
          error:
            message ||
            "Upload failed. Please try again with a supported image format.",
        };
      }
    } catch (error: any) {
      console.error("Upload failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Upload failed. Please try again with a supported image format.";
      return { error: errorMessage };
    }
  };

  const uploadMultipleFiles = async (files: File[]): Promise<PictureDTO[]> => {
    const body = new FormData();
    files.forEach((file) => body.append("files", file));

    const response = await AMBASSADOR_APIS.uploadMultipleFiles(body);
    const { status = false, data = [] } = response || {};

    if (status && Array.isArray(data)) {
      return data.map((file: any) => new PictureDTO(file));
    }

    return [];
  };

  const updateProfile = async (body: EditProfileAmbassador) => {
    const response = await AMBASSADOR_APIS.updateProfile(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      getUserProfile();
      navigate(-1);
    }
  };

  const getAdminAmbassadorEvents = async (
    setData: Function,
    queryParams: any = {}
  ) => {
    const response = await AMBASSADOR_APIS.getAdminAmbassadorEvents(
      queryParams
    );
    const { status = false, data = null } = response || {};
    if (status) {
      const { content = [] } = data || {};
      setData(content);
    }
  };

  const joinAmbassadorEvent = async (userId: number, body: any) => {
    const response = await AMBASSADOR_APIS.joinAmbassadorEvent(userId, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      getUserProfile();
      updateProfileCompletionStatus("ambassador");
      successToaster(message);
      navigate(siteRoutes.ambassadorHome);
    }
  };

  const getNearestSlot = async (setData: any) => {
    const response = await AMBASSADOR_APIS.getNearestSlot();
    const { status = false, data } = response || {};
    if (status) {
      setData(data);
      // navigate(siteRoutes.ambassadorHome)
    }
  };

  const postTeamBookSession = async (ambassadorId: number, body: any) => {
    const response = await AMBASSADOR_APIS.postTeamBookSession(
      ambassadorId,
      body
    );
    const { status = false, message = "" } = response || {};
    if (status) {
      getUserProfile();
      updateProfileCompletionStatus("ambassador");

      successToaster(message);
      navigate(siteRoutes.ambassadorHome);
    }
  };

  const getAmbassadorProducts = async (setData: any) => {
    const response = await AMBASSADOR_APIS.getAmbassadorProducts();
    const { status = false, data } = response || {};
    if (status) {
      setData(data);
    }
  };
  const getAmbassadorBadges = async (
    setData: Function,
    queryParams: any = {},
    setPagination?: Function
  ) => {
    const response = await AMBASSADOR_APIS.getAmbassadorBadges(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const {
        badges = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(Array.isArray(badges) ? badges : []);
      if (setPagination) {
        setPagination({
          page: pageNumber,
          size: pageSize,
          totalElements: totalElements,
        });
      }
    }
  };

  const updateAmbassadorTier = async (body: any) => {
    const response = await AMBASSADOR_APIS.updateAmbassadorTier(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getEarnedPoints = async (
    setData: Function,
    queryParams: any = {},
    setPagination?: Function
  ) => {
    const response = await AMBASSADOR_APIS.getEarnedPoints(queryParams);
    const { status = false, data = [] } = response || {};
    if (status) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(Array.isArray(content) ? content : []);
      if (setPagination) {
        setPagination({
          page: Number(pageNumber) || 0,
          size: Number(pageSize) || 10,
          totalElements: Number(totalElements) || 0,
        });
      }
    }
  };

  const getUsedPoints = async (
    setData: Function,
    queryParams: any = {},
    setPagination?: Function
  ) => {
    const response = await AMBASSADOR_APIS.getUsedPoints(queryParams);
    const { status = false, data = [] } = response || {};
    if (status) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(Array.isArray(content) ? content : []);
      if (setPagination) {
        setPagination({
          page: Number(pageNumber) || 0,
          size: Number(pageSize) || 10,
          totalElements: Number(totalElements) || 0,
        });
      }
    }
  };

  const postProductRequest = async (
    body: any,
    setShowPopup: Function,
    setShowPopupSecond: Function
  ) => {
    const response = await AMBASSADOR_APIS.postProductRequest(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      setShowPopup(false);
      setShowPopupSecond(true);
    }
  };

  const getActivity = async (
    setData: React.Dispatch<React.SetStateAction<any[]>>,
    body: { page: number; size: number },
    isAppend: boolean = false,
    setHasMore?: (hasMore: boolean) => void
  ) => {
    const response = await AMBASSADOR_APIS.getActivity(body);
    const { status = false, data = [] } = response || {};
    if (status) {
      const { content, totalElements, totalPages } = data;
      if (isAppend) {
        setData((prevData: any[]) => [...prevData, ...content]);
      } else {
        setData(content);
      }

      // Check if there are more pages
      if (setHasMore) {
        const currentPage = body.page;
        setHasMore(
          currentPage < totalPages - 1 && content.length === body.size
        );
      }
    }
  };
  const getPromotions = async (
    setData: React.Dispatch<React.SetStateAction<any[]>>,
    body: { page: number; size: number },
    isAppend: boolean = false,
    setHasMore?: (hasMore: boolean) => void
  ) => {
    const response = await AMBASSADOR_APIS.getPromotions(body);
    const { status = false, data = [] } = response || {};
    if (status) {
      const { content, totalElements, totalPages } = data;
      if (isAppend) {
        setData((prevData: any[]) => [...prevData, ...content]);
      } else {
        setData(content);
      }

      // Check if there are more pages
      if (setHasMore) {
        const currentPage = body.page;
        setHasMore(
          currentPage < totalPages - 1 && content.length === body.size
        );
      }
    }
  };

  const postNewPost = async (body: any) => {
    const response = await AMBASSADOR_APIS.postNewPost(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const postCreateShare = async (body: any) => {
    const response = await AMBASSADOR_APIS.postCreateShare(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getEventFollowUp = async (
    setData: Function,
    queryParams: any = {},
    setFilters?: Function
  ) => {
    const response = await AMBASSADOR_APIS.getEventFollowUp(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      // Extract pagination info - handle both number and string types
      const totalPages = typeof data.totalPages === 'string' ? parseInt(data.totalPages, 10) : (data.totalPages || 0);
      const totalElements = typeof data.totalElements === 'string' ? parseInt(data.totalElements, 10) : (data.totalElements || 0);
      
      // Update filters with pagination info
      if (setFilters) {
        setFilters((filters: any) => ({ 
          ...filters, 
          totalPages: totalPages, 
          totalElements: totalElements 
        }));
      }
      
      // Map events and set data - replace previous data
      const events = Array.isArray(data.content) 
        ? data.content.map((i: any) => new EventDTO(i))
        : [];
      
      setData(events);
    } else {
      // Clear data if API call fails
      setData([]);
    }
  };

  const postEventFollowUp = async (id: number, body: any) => {
    const response = await AMBASSADOR_APIS.postEventFollowUp(id, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getJoinAbleEvents = async (setData: Function) => {
    const response = await AMBASSADOR_APIS.getJoinAbleEvents();
    const { status = false, data = [] } = response || {};
    if (status) {
      const { content } = data;
      setData(content);
    }
  };

  const getEventDetails = async (id: number, setData: Function) => {
    const response = await AMBASSADOR_APIS.getEventDetails(id);
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(data);
    }
  };

  const postJoinMeeting = async (id: number, body: any) => {
    const response = await AMBASSADOR_APIS.postJoinMeeting(id, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      navigate(-1);
      successToaster(message);
    }
  };

  const postJoinEvents = async (id: number, body: any) => {
    const response = await AMBASSADOR_APIS.postJoinEvents(id, body);
    const { status = false, data = [], message = "" } = response || {};
    if (status) {
      successToaster(message);
      return response;
    } else {
      return { status: false };
    }
  };

  const getProfileData = async () => {
    const response = await AMBASSADOR_APIS.getUserProfile();
    const { status = false, message = "" } = response || {};

    if (status) {
      return response.data;
    }
  };

  const getAmbassadorExperienceById = async (id: number, setData: Function) => {
    const response = await AMBASSADOR_APIS.getAmbassadorExperienceById(id);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getAmbassadorCommunityMessages = async (
    id: number,
    setData: Function,
    params?: any
  ) => {
    try {
      const response = await AMBASSADOR_APIS.getAmbassadorCommunityMessages(
        id,
        params
      );
      const { status = false, data = {} } = response || {};
      if (status) {
        setData(data);
      }
      return response;
    } catch (error) {
      console.error("Error fetching ambassador community messages:", error);
      return null;
    }
  };

  const postAmbassadorCommunityMessages = async (id: number, body: any) => {
    try {
      const response = await AMBASSADOR_APIS.postAmbassadorCommunityMessages(
        id,
        body
      );
      const { status = false, message = "" } = response || {};
      if (status) {
        // successToaster(message);
      }
      return response;
    } catch (error) {
      console.error("Error posting ambassador community message:", error);
      return null;
    }
  };

  const markAttendanceAmbassadorExperience = async (id: number, body: any) => {
    const response = await AMBASSADOR_APIS.markAttendanceAmbassadorExperience(
      id,
      body
    );
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const toggleNotification = async () => {
    const response = await AMBASSADOR_APIS.toggleNotification();
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getAmbassadorProfile = async (setAmbassadorProfileData: Function) => {
    const response = await AMBASSADOR_APIS.getAmbassadorProfile();
    const { status = false, data } = response || {};
    if (status) {
      setAmbassadorProfileData({
        ...data.user,
        ...data.profiles.ambassador,
      });
    }
  };

  return {
    updateAmbassadorProfile,
    updateUserProfile,
    uploadFile,
    updateProfile,
    getNearestSlot,
    postTeamBookSession,
    getAmbassadorProducts,
    getAmbassadorBadges,
    updateAmbassadorTier,
    getEarnedPoints,
    getUsedPoints,
    postProductRequest,
    getActivity,
    getPromotions,
    uploadMultipleFiles,
    postNewPost,
    postCreateShare,
    getEventFollowUp,
    postEventFollowUp,
    getJoinAbleEvents,
    getEventDetails,
    postJoinMeeting,
    postJoinEvents,
    getProfileData,
    getAdminAmbassadorEvents,
    joinAmbassadorEvent,
    getAmbassadorExperienceById,
    getAmbassadorCommunityMessages,
    postAmbassadorCommunityMessages,
    markAttendanceAmbassadorExperience,
    toggleNotification,
    getAmbassadorProfile,
  };
};

export default useAmbassador;
